import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { GALLERY } from "../../lib/constants";
import { useReveal } from "../../hooks/useReveal";

const CATEGORY_MAP = {
  All: "All", Family: "Family", Couples: "Couples",
  Motherhood: "Motherhood", Children: "Children", Sunset: "Sunset",
  "Όλα": "All", "Οικογένεια": "Family", "Ζευγάρια": "Couples",
  "Μητρότητα": "Motherhood", "Παιδιά": "Children",
};

export const Gallery = () => {
  const { t } = useLanguage();
  const ref = useReveal();
  const [active, setActive] = useState(t.gallery.categories[0]);
  const [lightbox, setLightbox] = useState(null);

  // Reset filter when language changes
  React.useEffect(() => {
    setActive(t.gallery.categories[0]);
  }, [t.gallery.categories]);

  const filtered = useMemo(() => {
    const mapped = CATEGORY_MAP[active] || "All";
    if (mapped === "All") return GALLERY;
    return GALLERY.filter((g) => g.category === mapped);
  }, [active]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-bg-base py-16 md:py-20"
    >
      <div ref={ref} className="reveal max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="overline">{t.gallery.overline}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-accent-dark mt-4 tracking-tight font-light">
            {t.gallery.title}
          </h2>
          <p className="mt-5 text-text-muted text-base md:text-lg leading-relaxed">
            {t.gallery.subtitle}
          </p>
        </div>

        <div
          data-testid="gallery-filters"
          className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12"
        >
          {t.gallery.categories.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c}`}
              onClick={() => setActive(c)}
              className={`text-[11px] tracking-[0.2em] uppercase px-4 py-2 border transition-all ${
                active === c
                  ? "bg-accent-dark text-white border-accent-dark"
                  : "bg-transparent text-text-muted border-border-soft hover:border-gold hover:text-accent-dark"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="masonry">
          {filtered.map((g, i) => (
            <div
              key={`${active}-${i}`}
              data-testid={`gallery-item-${i}`}
              onClick={() => setLightbox(g)}
              className="masonry-item img-hover relative group"
            >
              <img
                src={g.src}
                alt={g.category}
                loading="lazy"
                className={`w-full ${
                  g.h === "tall" ? "aspect-[3/4]" : "aspect-[4/5]"
                } object-cover`}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-500" />
              <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <p className="text-[10px] tracking-[0.25em] uppercase text-white bg-accent-dark/80 backdrop-blur-sm px-2.5 py-1">
                  {g.category}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] bg-black/92 flex items-center justify-center p-6 fade-in"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
            aria-label="Close"
            data-testid="gallery-lightbox-close"
          >
            <X size={28} />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.category}
            className="max-h-[88vh] max-w-[92vw] object-contain"
          />
        </div>
      )}
    </section>
  );
};
